import React, { useState, useEffect } from "react";


const tabs = ["Personal Info", "Vehicle Info", "Insurance Details", "Financial Info"];

// --- Define Select Options ---
const CATEGORY_OPTIONS = ["Private Vehicle", "Commercial Vehicle", "Taxi", "Motorcycle", "Truck"];
const COVERAGE_OPTIONS = ["Liability", "Comprehensive", "Liability + Comprehensive", "Liability + Cargo Insurance", "IPT/IAC for taxis", "IPT/IAC for motorcyles" ];
const STATUS_OPTIONS = ["Active", "Inactive", "Pending", "Terminated"]; // Added Terminated

// 💡 1. ACCEPT initialData PROP AND DEFAULT IT TO AN EMPTY OBJECT
const AddCustomerForm = ({ onSubmit, onClose, initialData = {} }) => {
    const [activeTab, setActiveTab] = useState("Personal Info");


    // Check if we are in Edit mode
    const isEditing = Object.keys(initialData ?? {}).length > 0;
    
    // Define a base structure with default values
    const baseState = {
        // Personal Info...
        "Driver Name*": "", "ID Card Number Production Point*": "", "Driver's License Number*": "",
        "License Category": "", "Date of Birth": "", "Telephone Number": "",
        "Occupation": "", "Trade Register": "",
        // Vehicle Info...
        "Make*": "", "Registration Number": "", "Chassis Number": "",
        "Power and Energy": "", "Number of Seats": "", "Payload": "",
        "Attestation Number": "", "Pink Card Number": "", "Date of First Registration": "",
        "Date of Issue": "",
        // Insurance Details
        "Policy Number": "", 
        "Name of the Insured": "", // <-- Harmonized this field name
        "Category": "",
        "Driving Zone": "", "Effective Date": "", "Expiration Date": "",
        "Number of days": "", "Date of Resignation (Optional)": "", "Coverage Subscribed": "",
        "Advisors": "", 
        "Status": isEditing ? initialData["Status"] : "Active", // Default to 'Active' on new forms
        // Financial Info...
        "Net Premium (XAF)": "", "Accidental Damages(ACC) (XAF)": "", "Comission Fees(FC) (XAF)": "",
        "VAT (XAF)": "", "Claims Ratio(CR) (XAF)": "", "PTTC (XAF)": "",
        "Deffered Tax Asset(DTA) (XAF)": "", "Actual Collection (XAF)": "",
    };
    
    const [formData, setFormData] = 
    useState(() => {
        // Use spread syntax for a cleaner merge
        return isEditing ? { ...baseState, ...initialData } : baseState;
    });

    // Ensure data is updated when initialData changes
    useEffect (() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData(prev => ({
                ...prev,
                ...initialData,
            }));
        }
    }, [initialData]);

    const tabFields = {
        "Personal Info": [
            "Driver Name*", "ID Card Number Production Point*", "Driver's License Number*",
            "License Category", "Date of Birth", "Telephone Number",
            "Occupation", "Trade Register",
        ],
        "Vehicle Info": [
            "Make*", "Registration Number", "Chassis Number", "Power and Energy",
            "Number of Seats", "Payload", "Attestation Number", "Pink Card Number",
            "Date of First Registration", "Date of Issue",
        ],
        "Insurance Details": [
            "Policy Number", "Name of the Insured", "Category", 
            "Driving Zone", "Effective Date", "Expiration Date", 
            "Number of days", "Date of Resignation (Optional)", 
            "Coverage Subscribed",
            "Advisors", "Status",
        ],
        "Financial Info": [
            "Net Premium (XAF)", "Accidental Damages(ACC) (XAF)", "Comission Fees(FC) (XAF)",
            "VAT (XAF)", "Claims Ratio(CR) (XAF)", 
            "PTTC (XAF)",
            "Deffered Tax Asset(DTA) (XAF)", "Actual Collection (XAF)"],
    };

    const handleChange = (e, field) => {
        setFormData({ ...formData, [field]: e.target.value });
    };
    
    // ✅ CRITICAL FIX: Inject ID and Status for new records
    const handleSubmit = (e) => {
        e.preventDefault();

        // Perform basic validation (e.g., required fields) here if necessary
        // ...

        let customerData = { ...formData };

        if (!isEditing) {
            // For a new record, guarantee essential metadata
            customerData = {
                ...customerData,
                id: crypto.randomUUID(), // Guarantee a unique ID
                dateAdded: new Date().toISOString().split('T')[0], // Add creation date
                // Guarantee the status is Active if the form element was untouched
                "Status": customerData["Status"] || "Active", 
                expired: false, // Assume new contracts are not expired
            };
        }
        
        // Pass the final customer object up to the parent component for saving
        onSubmit(customerData); 
        onClose();
    };

    return(
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <form
                onSubmit={handleSubmit}
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6"
            >
                {/* 💡 3. DYNAMIC TITLE */}
                <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b pb-2">
                    {isEditing ? "Edit Customer" : "Add New Customer"}
                </h2>

                {/* Tabs Navigation (remains the same) */}
                <div className="flex border-b mb-6 sticky top-0 bg-white z-10">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            className={`px-4 py-3 -mb-px text-sm font-medium border-b-2 transition duration-150 ease-in-out ${
                                activeTab === tab 
                                ? "border-gray-900 text-gray-900" 
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Fields Grid (remains the same) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 pb-4">
                    {tabFields[activeTab].map((field) => (
                        <div key={field} className="col-span-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {field.replace(/\*/g, "")}
                                {field.includes("*") && <span className="text-red-500">*</span>}
                            </label>

                            {/* --- Conditional Rendering for Select Dropdowns --- */}
                            {field === "Category" || field === "Coverage Subscribed" || field === "Status" ? (
                                <select
                                    name={field}
                                    value={formData[field]}
                                    onChange={(e) => handleChange(e, field)}
                                    required={field.includes("*")}
                                    className="border border-gray-300 rounded-lg p-2 w-full focus:ring-gray-900 focus:border-gray-900 outline-none transition appearance-none"
                                    // Custom styling to replace the default dropdown arrow
                                    style={{ 
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236B7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd' /%3E%3C/svg%3E")`,
                                        backgroundPosition: 'right 0.5rem center',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: '1.5em 1.5em',
                                        paddingRight: '2.5rem'
                                    }}
                                >
                                    {/* The default value is set in baseState, but we keep this empty option for UX */}
                                    <option value="" disabled>Select {field.replace(/\*| \(.+\)/, "").toLowerCase()}</option>
                                    {(field === "Category" ? CATEGORY_OPTIONS : field === "Coverage Subscribed" ? COVERAGE_OPTIONS : STATUS_OPTIONS).map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            ) : (
                                // --- RENDER REGULAR INPUT ---
                                <input
                                    type={field.toLowerCase().includes("date") ? "date" : "text"}
                                    name={field}
                                    value={formData[field]}
                                    onChange={(e) => handleChange(e, field)}
                                    required={field.includes("*")}
                                    className="border border-gray-300 rounded-lg p-2 w-full focus:ring-gray-900 focus:border-gray-900 outline-none transition"
                                />
                            )} 
                        </div>
                    ))}
                </div>

                {/* Buttons */}
                <div className="mt-8 pt-4 border-t flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition duration-150"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-6 py-2 bg-gray-900 text-white rounded-lg shadow hover:bg-gray-700 transition duration-150"
                    >
                        {/* 💡 4. DYNAMIC BUTTON TEXT */}
                        {isEditing ? "Update Customer" : "Save Customer"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddCustomerForm;
